import { Route, Switch, Redirect, useHistory, useLocation } from 'react-router-dom';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { hasFormErrors } from 'utils/GiftFlowHelper';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { createGiftCard } from 'utils/requests/gifting_requests';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import { pushHistory, localizedRouteIncludes } from 'utils/client_side_routing_helpers';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import { FormattedMessage } from 'react-intl';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import useCountryId from 'hooks/useCountryId';
import User from '@alltrails/shared/types/User';
import { useDispatch } from 'react-redux';
import useUser from 'hooks/useUser';
import * as styles from './styles/styles.module.scss';
import CustomizeGiftForm from '../CustomizeGiftForm';
import { UPDATE_SERVER_ERRORS, UPDATE_PURCHASED_GIFT_CARD, useGiftFlowContext } from '../GiftFlow/GiftFlow';
import GiftLandingPage from '../Landing/GiftLandingPage';
import LoginForm from '../../forms/LoginForm';
import GiftCardPrint from '../GiftCardPrint';
import GiftConfirmation from '../GiftConfirmation';
import SignUpForm from '../../forms/SignUpForm';
import PaymentForm from '../../forms/pro/PaymentForm';
import MiniFooter from '../MiniFooter';
import GiftReview from '../GiftReview';
import { setUser } from '../../../userSlice';

const {
  strings: {
    GIFT_FLOW: {
      PAYMENT_FORM: { SUBMIT_TEXT }
    }
  }
} = StringConstantsMixin;

const MINI_FOOTER_PATHNAMES = ['/gift/payment', '/gift/login', '/gift/signup'];
const INVALID_EMAIL = 'invalid_email';
const UNITED_STATES_COUNTRY_ID = 313;

type PaymentType = {
  name: string;
  tab: number;
  iconPath: string;
};

type Props = {
  currencyCode?: CurrencyCode;
  planTypes: Plan[];
  shopifyProducts?: unknown;
  recurlyPublicKey: string;
  paymentTypes: PaymentType[];
};

const GiftFlowRouting = ({ currencyCode, planTypes, recurlyPublicKey, paymentTypes, shopifyProducts }: Props) => {
  const dispatch = useDispatch();
  const languageRegionCode = useLanguageRegionCode();
  const countryId = useCountryId();
  const user = useUser();

  const {
    giftFlowState: { giftCustomizeParams, activePlanType, purchasedGiftCard },
    dispatchGiftFlow
  } = useGiftFlowContext();

  const history = useHistory();
  const location = useLocation();

  const handleAuthSuccess = ({ user }: { user: User }) => {
    dispatch(setUser(user));
    pushHistory('/gift/payment', history, languageRegionCode);
  };

  const navigateToSignUp = (e: any) => {
    e.preventDefault();
    pushHistory('/gift/signup', history, languageRegionCode);
  };

  const navigateToLogin = () => {
    pushHistory('/gift/login', history, languageRegionCode);
  };

  const handlePaymentSuccess = (payload: any) => {
    Object.assign(payload, { isEmail: !!payload.metadata.deliveryDate });
    dispatchGiftFlow({ type: UPDATE_PURCHASED_GIFT_CARD, payload });

    pushHistory('/gift/review', history, languageRegionCode);
  };

  const handlePaymentError = (err: any) => {
    if (!err || !err.responseJSON) {
      return err;
    }

    const { errors } = err.responseJSON;
    const invalidEmailErrors = errors ? errors.filter((error: any) => error.code === INVALID_EMAIL) : [];

    if (invalidEmailErrors.length > 0) {
      dispatchGiftFlow({ type: UPDATE_SERVER_ERRORS, payload: invalidEmailErrors });
      pushHistory('/gift/customize', history, languageRegionCode);
    }

    return err;
  };

  const handlePaymentSubmit = (creditCardInfo: any) =>
    createGiftCard(creditCardInfo, giftCustomizeParams, currencyCode, languageRegionCode).then(handlePaymentSuccess).catch(handlePaymentError);

  return (
    <>
      <Switch>
        <Route path="*/gift" exact>
          <GiftLandingPage currencyCode={currencyCode} planTypes={planTypes} />
        </Route>
        <Route path="*/gift/customize" exact>
          <CustomizeGiftForm />
        </Route>
        <Route path="*/gift/login" exact>
          {user ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              <LoginForm handleLoginSuccess={handleAuthSuccess} handleRegisterClick={navigateToSignUp} />
            </div>
          )}
        </Route>
        <Route path="*/gift/signup" exact>
          {user ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              <SignUpForm handleRegistrationSuccess={handleAuthSuccess} handleLoginClick={navigateToLogin} />
            </div>
          )}
        </Route>
        <Route path="*/gift/payment" exact>
          {hasFormErrors(giftCustomizeParams) ? (
            <Redirect to={wrapUrlSafe('/gift/customize', languageRegionCode)} />
          ) : (
            <div className={styles.formContainer}>
              {giftCustomizeParams.sendMethod === 'email' && (
                <GiftConfirmation
                  recipientName={giftCustomizeParams.recipientName}
                  recipientEmail={giftCustomizeParams.recipientEmail}
                  sendDate={giftCustomizeParams.sendDate}
                  languageRegionCode={languageRegionCode}
                />
              )}
              <PaymentForm
                headText={<FormattedMessage defaultMessage="Enter payment details" />}
                submitText={SUBMIT_TEXT}
                recurlyPublicKey={recurlyPublicKey}
                plan={planTypes[0]}
                isCouponInputExpanded={false}
                isCouponInputShown={false}
                paymentTypes={paymentTypes}
                currencyCode={currencyCode}
                handlePayment={handlePaymentSubmit}
                webProPurchaseLocation={WebProPurchaseLocation.GiftPage}
                hideFinePrint
                showPayWithCard
              />
            </div>
          )}
        </Route>
        <Route path="*/gift/review" exact>
          {!purchasedGiftCard ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <GiftReview {...purchasedGiftCard} shopifyProducts={shopifyProducts} showShopifyPreview={countryId === UNITED_STATES_COUNTRY_ID} />
          )}
        </Route>
        <Route path="*/gift/preview" exact>
          {!purchasedGiftCard ? (
            <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
          ) : (
            <GiftCardPrint {...purchasedGiftCard} languageRegionCode={languageRegionCode} />
          )}
        </Route>
        <Route path="*/gift*" exact>
          <Redirect to={wrapUrlSafe('/gift', languageRegionCode)} />
        </Route>
      </Switch>
      {localizedRouteIncludes(location.pathname, MINI_FOOTER_PATHNAMES, languageRegionCode) && <MiniFooter />}
    </>
  );
};

GiftFlowRouting.defaultProps = {
  currencyCode: 'USD',
  shopifyProducts: []
};

export default GiftFlowRouting;
